import CommonModal from 'components/common/CommonModal';
import DefaultButton from 'components/common/DefaultButton';
import DefaultImageView from 'components/common/DefaultImageView';
import DefaultText from 'components/common/DefaultText';
import FileInputButton from 'components/common/FileInputButton';
import {changeBusinessRegisterNumberImg} from 'function/api';
import {convertPdfToImage, handleDownloadClick} from 'function/converter';
import React, {useEffect, useState} from 'react';
import {useRecoilValue} from 'recoil';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {userInfoState} from 'utils/Atoms';
import {s3GetCognito} from 'utils/cognito';
import {minmax} from 'utils/minmax';

/**
 * 사업자 이미지 모달 컴포넌트의 Props 타입
 * @property {any} selectItem - 선택된 항목
 * @property {() => void} closeModal - 모달 닫기 함수
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsLoading - 로딩 상태 설정 함수
 * @property {React.Dispatch<React.SetStateAction<{}>>} setErrorModal - 에러 모달 설정 함수
 */
type Props = {
  selectItem: any;
  closeModal: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
};

/**
 * 사업자 이미지 모달 컴포넌트
 * 사업자등록증 이미지를 표시하고 관리하는 모달 컴포넌트입니다.
 * @param {Props} props - 사업자 이미지 모달 컴포넌트의 props
 * @returns {JSX.Element} 사업자 이미지 모달 컴포넌트
 */
const BusinessImgModal = ({
  selectItem,
  closeModal,
  setIsLoading,
  setErrorModal,
}: Props) => {
  const userInfo = useRecoilValue(userInfoState);
  const [formatImg, setFormatImg] = useState('');
  const [completeModal, setCompleteModal] = useState(false);

  useEffect(() => {
    const businessRegisterNumberImg =
      selectItem.createParams?.businessRegisterNumberImg ||
      selectItem.businessRegisterNumberImg ||
      '';

    if (businessRegisterNumberImg) {
      setIsLoading(true);
      s3GetCognito({
        isTransactions: selectItem.createParams ? true : false,
        uuid: selectItem.uuid,
        imgUrls: [businessRegisterNumberImg],
        type: selectItem.type,
        userInfo,
      })
        .then((result: any) => {
          result && setFormatImg(result[0]);
          setIsLoading(false);
        })
        .catch((error: any) => {
          setIsLoading(false);
        });
    }

    return () => {
      setFormatImg('');
    };
  }, [selectItem]);

  return (
    <>
      <CommonModal containerStyle={{height: '60vh'}} closeModal={closeModal}>
        <BusinessModalContainer>
          <BusinessModalHeader>
            <DefaultText
              text="businessLicense"
              textStyle={{
                fontSize: minmax(40),
              }}
            />
            <DefaultImageView
              source="images/common/close.png"
              onClick={closeModal}
              style={{
                width: minmax(30),
                height: minmax(30),
                margin: minmax(15),
                cursor: 'pointer',
              }}
            />
          </BusinessModalHeader>

          {formatImg && (
            <DefaultImageView
              source={formatImg}
              style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                objectFit: 'contain',
                border: `1px solid ${colors.borderGray}`,
                borderRadius: minmax(10),
                backgroundColor: colors.lighterGray,
              }}
            />
          )}
          <ButtonsContainer>
            <FileInputButton
              setValue={async (file: File) => {
                const newFile = (
                  file
                    ? file.type.includes('pdf')
                      ? await convertPdfToImage(file)
                      : file.type.includes('image')
                      ? file
                      : null
                    : null
                ) as File | null;
                // 변경 api 호출
                if (newFile) {
                  const result = await changeBusinessRegisterNumberImg({
                    userInfo,
                    setErrorModal,
                    setIsLoading,
                    contractorUUID: selectItem.uuid,
                    businessRegisterNumberImg: newFile,
                  });
                  if (result) setCompleteModal(true);
                }
              }}
              accept="image/*, .pdf"
              title={'change'}
              active
            />

            <DefaultButton
              text="download"
              actived={1}
              buttonStyle={{
                width: '100%',
                backgroundColor: colors.primary2,
              }}
              onClick={async () => {
                await handleDownloadClick({
                  url: selectItem.businessRegisterNumberImg,
                  name: selectItem.name + '_사업자등록증.jpg',
                  userInfo,
                  type: selectItem.type,
                  uuid: selectItem.uuid,
                });
              }}
            />
          </ButtonsContainer>
        </BusinessModalContainer>
      </CommonModal>

      {completeModal && (
        <CommonModal
          title="사업자등록증 변경 성공"
          detail="사업자등록증 변경이 완료되었습니다!"
          firstButtonText="confirm"
          firstButtonClick={() => {
            setCompleteModal(false);
            closeModal();
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

export default BusinessImgModal;

const BusinessModalContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  padding: ${minmax(20)};
  display: flex;
  flex-direction: column;
`;

const BusinessModalHeader = styled.div`
  width: 100%;
  margin-bottom: ${minmax(10)};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;
